"use client";

import { useEffect, useRef, useState } from "react";

import { COMANDO_ICONA } from "@/components/article/ShareBar";
import { FUOCO_ED } from "@/components/editoriali/stile";
import { Icona } from "@/components/home/Icona";
import { ORIGINE_CANONICA_VERIFICATA, ORIGINI_PUBBLICHE_VERIFICATE } from "@/lib/site";

/** Il bottone di testo del riferimento (`.reading-tools button`): Geist a
 *  12, muto, senza fondo, alto almeno 32 per il tocco. */
const COMANDO = `inline-flex min-h-[32px] cursor-pointer items-center gap-1.5 border-0 bg-transparent px-1 font-sans text-meta text-muted transition-colors hover:text-ink ${FUOCO_ED}`;

/** Siamo su un'anteprima e non sul sito? Una porta esplicita, localhost o
 *  un indirizzo IP: sono i server di prova (`next start` su una porta
 *  libera), da cui l'indirizzo da condividere non è quello che si sta
 *  leggendo. */
function inAnteprima(): boolean {
  const h = location.hostname;
  return (
    location.port !== "" ||
    h === "localhost" ||
    h === "[::1]" ||
    /^\d{1,3}(\.\d{1,3}){3}$/.test(h)
  );
}

type Esito = { url: string } | { errore: string };

/**
 * Quale indirizzo finisce negli appunti, nell'ordine deciso il 02/10/2026:
 *
 * 1. il canonico, ma solo se `NEXT_PUBLIC_SITE_URL` punta a un'origine
 *    **verificata** (`ORIGINI_PUBBLICHE_VERIFICATE` in lib/site.ts): la
 *    sola presenza della variabile non basta, e il ripiego di `SITE_URL`
 *    è un dominio ipotizzato;
 * 2. sul sito pubblico, l'origine corrente: è quella da cui si legge;
 * 3. da un'anteprima, l'indirizzo pubblico verificato del pezzo, mai
 *    quello dell'anteprima. Se il pezzo non è pubblico (un testo di prova,
 *    un dispaccio datato in avanti) lo si dice, invece di copiare un
 *    indirizzo che altrove non porta a niente.
 */
function indirizzoDaCopiare(href: string, pubblico: boolean): Esito {
  const nonPubblico = { errore: "Il collegamento pubblico non è disponibile: questo pezzo non è ancora pubblicato." };
  if (ORIGINE_CANONICA_VERIFICATA) return pubblico ? { url: ORIGINE_CANONICA_VERIFICATA + href } : nonPubblico;
  if (!inAnteprima()) return { url: location.origin + href };
  const pubblica = ORIGINI_PUBBLICHE_VERIFICATE[0];
  if (!pubblico || !pubblica) return nonPubblico;
  return { url: pubblica + href };
}

/** Negli appunti, anche dove `navigator.clipboard` non c'è: l'API vuole
 *  un'origine sicura (https o localhost), e un'anteprima su
 *  `http://host:porta` non lo è. Lì si ripiega su una `<textarea>`
 *  selezionata e `execCommand("copy")`, che è deprecato ma funziona. */
async function negliAppunti(testo: string): Promise<boolean> {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(testo);
      return true;
    }
  } catch {
    /* si prova la strada vecchia */
  }
  const ta = document.createElement("textarea");
  ta.value = testo;
  ta.setAttribute("readonly", "");
  ta.style.position = "fixed";
  ta.style.top = "-1000px";
  document.body.appendChild(ta);
  ta.select();
  let ok = false;
  try {
    ok = document.execCommand("copy");
  } catch {
    ok = false;
  }
  ta.remove();
  return ok;
}

/**
 * «Copia link» e «Stampa», a destra della riga sopra il testo
 * (`.reading-tools` del riferimento).
 *
 * L'esito della copia lo dice una riga `role="status"`, che i lettori di
 * schermo annunciano senza spostare il fuoco: riuscita (con l'indirizzo
 * copiato, così si vede quale), fallita (con l'indirizzo scritto, da
 * copiare a mano) o non disponibile. Sparisce dopo otto secondi.
 *
 * «Stampa» apre la finestra del browser; quello che esce su carta lo
 * decide il blocco di stampa in globals.css (solo il pezzo, testo scuro
 * su bianco).
 */
export function ComandiLettura({
  href,
  pubblico,
  aspetto = "riga",
}: {
  href: string;
  pubblico: boolean;
  /** `riga`: i bottoni muti del riferimento del 02/10. `mare`: quelli
   *  della pagina del singolo editoriale, con l'icona. */
  aspetto?: "riga" | "mare";
}) {
  const [stato, setStato] = useState<{ tipo: "ok" | "errore"; testo: string } | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current);
  }, []);

  function mostra(tipo: "ok" | "errore", testo: string) {
    setStato({ tipo, testo });
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setStato(null), 8000);
  }

  async function copia() {
    const esito = indirizzoDaCopiare(href, pubblico);
    if ("errore" in esito) return mostra("errore", esito.errore);
    const ok = await negliAppunti(esito.url);
    if (ok) mostra("ok", `Link copiato: ${esito.url}`);
    else mostra("errore", `Copia non riuscita. L’indirizzo è ${esito.url}`);
  }

  return (
    <div className={`flex items-center ${aspetto === "mare" ? "gap-2" : "gap-[18px] max-sm:gap-2"}`}>
      {aspetto === "mare" ? (
        <>
          {/* Solo icone, come i comandi del dispaccio (variante `icone` di
              ShareBar, stesso `COMANDO_ICONA`): richiesta del cliente del
              26/10/2026. Il nome sta in `title` e `aria-label`. */}
          <button type="button" onClick={copia} className={COMANDO_ICONA} title="Copia link" aria-label="Copia il link">
            <Icona nome="collegamento" className="size-[18px]" />
          </button>
          <button
            type="button"
            onClick={() => window.print()}
            className={COMANDO_ICONA}
            title="Stampa"
            aria-label="Stampa l’editoriale"
          >
            <Icona nome="stampa" className="size-[18px]" />
          </button>
        </>
      ) : (
        <>
          <button type="button" onClick={copia} className={COMANDO}>
            Copia link <span aria-hidden>↗</span>
          </button>
          <button type="button" onClick={() => window.print()} className={COMANDO}>
            Stampa
          </button>
        </>
      )}
      {/* La riga d'esito sta sotto la riga dei comandi, in assoluto come
          nel riferimento (`#status`): così comparendo non sposta il testo
          del pezzo. A destra, sotto i bottoni che l'hanno prodotta. */}
      <p
        role="status"
        aria-live="polite"
        className={`absolute top-full right-0 m-0 mt-1 max-w-full text-right font-sans text-meta leading-[1.4] break-all ${
          stato?.tipo === "errore" ? "text-ink" : aspetto === "mare" ? "text-mare" : "text-accent"
        }`}
      >
        {stato?.testo ?? ""}
      </p>
    </div>
  );
}

/** Il bottone del pannello «Accedi» per gli editoriali riservati: lo
 *  stesso evento che usa DispaccioView (`oi:apri-accesso`), cioè il sistema
 *  d'accesso del sito e non uno disegnato qui. Il corpo di un riservato
 *  non lascia mai il server, chiunque sia «entrato». */
export function ApriAccesso() {
  return (
    <button
      type="button"
      onClick={() => {
        window.scrollTo({ top: 0 });
        window.dispatchEvent(new Event("oi:apri-accesso"));
      }}
      className={`cursor-pointer rounded-[2px] border-0 bg-accent px-[14px] py-[11px] font-sans text-meta leading-[1.4] whitespace-nowrap text-onAccent transition-opacity hover:opacity-90 ${FUOCO_ED}`}
    >
      Accedi <span aria-hidden>↗</span>
    </button>
  );
}
